/**
 * Staff Assignment Tab Component
 * Manages staff-to-children assignments with printable reports
 * Supports filtering by category and mixed ratio selection
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Printer, Trash2, Download, Plus, Info, Save, X } from "lucide-react";
import { format, parseISO } from "date-fns";
import { exportStaffAssignmentPDF } from "@/utils/pdfExport";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const CATEGORIES = [
  { value: "full_time", label: "Full Time" },
  { value: "part_time", label: "Part Time" },
  { value: "school_age", label: "School Age" },
  { value: "after_school", label: "After School Program" },
  { value: "other", label: "Other" },
  { value: "mixed_category", label: "Mixed Category" }
];

const CLASSROOMS = [
  { value: "prek", label: "PreK" },
  { value: "young_toddler", label: "Young Toddler" },
  { value: "older_toddler", label: "Older Toddler" },
  { value: "infant", label: "Infant" },
  { value: "school_age", label: "School Age" },
  { value: "mixed_age_group", label: "Mixed Age Group" }
];

export const StaffAssignmentTab = ({ staff, children, centerName = '', canViewAllStaff = true, currentUser = null }) => {
  const [selectedStaff, setSelectedStaff] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedClassroom, setSelectedClassroom] = useState("");
  const [assignedChildren, setAssignedChildren] = useState([]);
  const [staffNotes, setStaffNotes] = useState("");
  const [rosterDate, setRosterDate] = useState(() => format(new Date(), 'MMMM d, yyyy'));
  const [isSaving, setIsSaving] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [hasSavedRoster, setHasSavedRoster] = useState(false);
  const [lastModifiedByName, setLastModifiedByName] = useState("");
  const [lastModifiedByRole, setLastModifiedByRole] = useState("");
  const [lastModifiedAt, setLastModifiedAt] = useState("");

  // For Mixed Age Group - which classrooms to include
  const [mixedClassrooms, setMixedClassrooms] = useState([]);
  // For Mixed Category - which categories to include
  const [mixedCategories, setMixedCategories] = useState([]);

  // Mark roster as read when page opens (clears sidebar notification dot)
  useEffect(() => {
    if (!canViewAllStaff && currentUser?.id) {
      axios.post(`${API}/staff-roster/mark-read`).catch(() => {});
    }
  }, [canViewAllStaff, currentUser]);

  // Auto-fill staff name for non-director roles
  useEffect(() => {
    if (!canViewAllStaff && currentUser?.id) {
      setSelectedStaff(currentUser.id);
    }
  }, [canViewAllStaff, currentUser]);

  // Load saved roster when staffId is known
  useEffect(() => {
    const staffId = canViewAllStaff ? selectedStaff : currentUser?.id;
    if (!staffId) return;
    axios.get(`${API}/staff-roster`, { params: { staffId } })
      .then(res => {
        const r = res.data?.roster;
        if (!r) { setHasSavedRoster(false); return; }
        setHasSavedRoster(true);
        setSelectedClassroom(r.classroom || "");
        setSelectedCategory(r.category || "");
        setStaffNotes(r.notes || "");
        setAssignedChildren(r.children || []);
        setLastModifiedByName(r.lastModifiedByName || "");
        setLastModifiedByRole(r.lastModifiedByRole || "");
        setLastModifiedAt(r.lastModifiedAt || "");
        if (r.createdAt) {
          try { setRosterDate(format(parseISO(r.createdAt), 'MMMM d, yyyy')); } catch {}
        }
      })
      .catch(() => setHasSavedRoster(false));
  }, [selectedStaff, canViewAllStaff, currentUser]);

  const handleSave = async () => {
    const staffId = canViewAllStaff ? selectedStaff : currentUser?.id;
    if (!staffId) { toast.error("Select a staff member first"); return; }
    setIsSaving(true);
    try {
      await axios.post(`${API}/staff-roster`, {
        staffId,
        staffName: staff.find(s => s.id === staffId)?.name || currentUser?.name || "",
        classroom: selectedClassroom,
        category: selectedCategory,
        notes: staffNotes,
        children: assignedChildren.map(c => ({
          childId: c.childId || c.id || "",
          childName: c.name || c.childName || "",
          birthdate: c.birthdate || "",
          age: c.age || "",
          category: c.assignedCategory || c.category || "",
          classroom: c.assignedClassroom || c.classroom || ""
        }))
      });
      const now = format(new Date(), 'MMMM d, yyyy');
      setRosterDate(now);
      setHasSavedRoster(true);
      toast.success("Roster saved");
    } catch { toast.error("Failed to save roster"); }
    finally { setIsSaving(false); }
  };

  const handleClear = async () => {
    if (!window.confirm("Clear this saved roster? This cannot be undone.")) return;
    const staffId = canViewAllStaff ? selectedStaff : currentUser?.id;
    setIsClearing(true);
    try {
      await axios.delete(`${API}/staff-roster`, { params: { staffId } });
      setAssignedChildren([]);
      setSelectedClassroom("");
      setSelectedCategory("");
      setStaffNotes("");
      setRosterDate(format(new Date(), 'MMMM d, yyyy'));
      setHasSavedRoster(false);
      setLastModifiedByName("");
      setLastModifiedByRole("");
      setLastModifiedAt("");
      toast.success("Roster cleared");
    } catch { toast.error("Failed to clear roster"); }
    finally { setIsClearing(false); }
  };
  
  // Debug: log children data on mount
  useEffect(() => {
    console.log("StaffAssignmentTab - Children received:", children?.length);
    console.log("StaffAssignmentTab - Sample child:", children?.[0]);
    
    // Count by classroom/age group (fullTimeSubcategory)
    const classroomCounts = {};
    const categoryCounts = {};
    children?.forEach(c => {
      const classroom = c.fullTimeSubcategory || 'unassigned';
      const cat = c.category || 'uncategorized';
      classroomCounts[classroom] = (classroomCounts[classroom] || 0) + 1;
      categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
    });
    console.log("StaffAssignmentTab - Classroom/Age Group counts:", classroomCounts);
    console.log("StaffAssignmentTab - Category counts:", categoryCounts);
  }, [children]);

  const getCategoryName = (value) => CATEGORIES.find(c => c.value === value)?.label || value || "";
  const getClassroomName = (value) => CLASSROOMS.find(c => c.value === value)?.label || value || "";
  const getStaffName = (id) => staff.find(s => s.id === id)?.name || "Not Selected";

  // Get filtered children based on CLASSROOM/AGE GROUP selection (using fullTimeSubcategory)
  const getFilteredChildrenByClassroom = () => {
    if (!selectedClassroom || selectedClassroom === 'mixed_age_group') return [];
    
    return children.filter(child => {
      if (child.active === false) return false;
      
      // Get the child's classroom/age group (fullTimeSubcategory)
      const childClassroom = (child.fullTimeSubcategory || '').toLowerCase().replace(/\s+/g, '_');
      
      // Check if classroom matches
      return childClassroom === selectedClassroom;
    });
  };

  // Get filtered children based on CATEGORY selection
  const getFilteredChildrenByCategory = () => {
    if (!selectedCategory || selectedCategory === 'mixed_category') return [];
    
    return children.filter(child => {
      if (child.active === false) return false;
      
      // Normalize the child's category for comparison
      const childCategory = (child.category || '').toLowerCase().replace(/\s+/g, '_');
      
      // Check if category matches
      return childCategory === selectedCategory;
    });
  };

  // Get filtered children for Mixed Age Group (multiple classrooms)
  const getMixedFilteredChildrenByClassroom = () => {
    if (mixedClassrooms.length === 0) return [];
    
    return children.filter(child => {
      if (child.active === false) return false;
      
      const childClassroom = (child.fullTimeSubcategory || '').toLowerCase().replace(/\s+/g, '_');
      return mixedClassrooms.includes(childClassroom);
    });
  };

  // Get filtered children for Mixed Category
  const getMixedFilteredChildrenByCategory = () => {
    if (mixedCategories.length === 0) return [];
    
    return children.filter(child => {
      if (child.active === false) return false;
      
      const childCategory = (child.category || '').toLowerCase().replace(/\s+/g, '_');
      return mixedCategories.includes(childCategory);
    });
  };

  // Calculate age from birthdate
  const calculateAge = (birthdate) => {
    if (!birthdate) return null;
    try {
      const birthDate = parseISO(birthdate);
      const today = new Date();
      let years = today.getFullYear() - birthDate.getFullYear();
      let months = today.getMonth() - birthDate.getMonth();
      
      if (months < 0) {
        years--;
        months += 12;
      }
      if (today.getDate() < birthDate.getDate()) {
        months--;
        if (months < 0) {
          years--;
          months += 12;
        }
      }
      
      if (years > 0) {
        return `${years}y ${months}m`;
      }
      return `${months}m`;
    } catch {
      return null;
    }
  };

  // Handle adding children from a single classroom/age group
  const handleAddChildrenByClassroom = () => {
    const filtered = getFilteredChildrenByClassroom();
    console.log("Adding children - Classroom:", selectedClassroom, "Found:", filtered.length);
    
    if (filtered.length === 0) {
      console.log("No children found for classroom:", selectedClassroom);
      return;
    }
    
    const newChildren = filtered
      .filter(c => !assignedChildren.find(ac => ac.id === c.id))
      .map(child => ({
        ...child,
        age: calculateAge(child.birthdate),
        assignedClassroom: getClassroomName(child.fullTimeSubcategory),
        assignedCategory: getCategoryName(child.category)
      }));
    
    setAssignedChildren([...assignedChildren, ...newChildren]);
  };

  // Handle adding children from a single category
  const handleAddChildrenByCategory = () => {
    const filtered = getFilteredChildrenByCategory();
    console.log("Adding children - Category:", selectedCategory, "Found:", filtered.length);
    
    if (filtered.length === 0) {
      console.log("No children found for category:", selectedCategory);
      return;
    }
    
    const newChildren = filtered
      .filter(c => !assignedChildren.find(ac => ac.id === c.id))
      .map(child => ({
        ...child,
        age: calculateAge(child.birthdate),
        assignedClassroom: getClassroomName(child.fullTimeSubcategory),
        assignedCategory: getCategoryName(child.category)
      }));
    
    setAssignedChildren([...assignedChildren, ...newChildren]);
  };

  // Handle adding children from mixed age groups
  const handleAddMixedChildrenByClassroom = () => {
    const filtered = getMixedFilteredChildrenByClassroom();
    console.log("Adding mixed children - Classrooms:", mixedClassrooms, "Found:", filtered.length);
    
    if (filtered.length === 0) {
      return;
    }
    
    const newChildren = filtered
      .filter(c => !assignedChildren.find(ac => ac.id === c.id))
      .map(child => ({
        ...child,
        age: calculateAge(child.birthdate),
        assignedClassroom: getClassroomName(child.fullTimeSubcategory),
        assignedCategory: getCategoryName(child.category)
      }));
    
    setAssignedChildren([...assignedChildren, ...newChildren]);
  };

  // Handle adding children from mixed categories
  const handleAddMixedChildrenByCategory = () => {
    const filtered = getMixedFilteredChildrenByCategory();
    console.log("Adding mixed children - Categories:", mixedCategories, "Found:", filtered.length);
    
    if (filtered.length === 0) {
      return;
    }
    
    const newChildren = filtered
      .filter(c => !assignedChildren.find(ac => ac.id === c.id))
      .map(child => ({
        ...child,
        age: calculateAge(child.birthdate),
        assignedClassroom: getClassroomName(child.fullTimeSubcategory),
        assignedCategory: getCategoryName(child.category)
      }));
    
    setAssignedChildren([...assignedChildren, ...newChildren]);
  };

  const handleRemoveChild = (childId) => {
    setAssignedChildren(assignedChildren.filter(c => (c.id || c.childId) !== childId));
  };

  const handleClearAll = () => {
    setAssignedChildren([]);
  };

  const toggleMixedClassroom = (classroomValue) => {
    setMixedClassrooms(prev => {
      if (prev.includes(classroomValue)) {
        return prev.filter(c => c !== classroomValue);
      } else {
        return [...prev, classroomValue];
      }
    });
  };

  const toggleMixedCategory = (categoryValue) => {
    setMixedCategories(prev => {
      if (prev.includes(categoryValue)) {
        return prev.filter(c => c !== categoryValue);
      } else {
        return [...prev, categoryValue];
      }
    });
  };

  const handlePrint = () => {
    window.print();
  };

  // Check if we're in Mixed modes
  const isMixedAgeGroup = selectedClassroom === 'mixed_age_group';
  const isMixedCategory = selectedCategory === 'mixed_category';

  // Get available children count for display
  const availableByClassroom = isMixedAgeGroup 
    ? getMixedFilteredChildrenByClassroom().length 
    : getFilteredChildrenByClassroom().length;
  
  const availableByCategory = isMixedCategory
    ? getMixedFilteredChildrenByCategory().length
    : getFilteredChildrenByCategory().length;

  return (
    <Card className="bg-white/90 backdrop-blur-sm border-2 border-teal-200">
      <CardContent className="pt-6">
        {/* Form Controls */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 print:hidden">
          <div>
            <Label className="text-teal-800 font-semibold">Staff Member</Label>
            {canViewAllStaff ? (
              <Select value={selectedStaff} onValueChange={setSelectedStaff}>
                <SelectTrigger className="border-teal-200 mt-1">
                  <SelectValue placeholder="Select staff member" />
                </SelectTrigger>
                <SelectContent>
                  {staff.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="mt-1 px-3 py-2 rounded-md border border-teal-200 bg-teal-50 text-teal-900 font-medium text-sm">
                {staff.find(s => s.id === currentUser?.id)?.name || currentUser?.name || "You"}
              </div>
            )}
          </div>

          <div>
            <Label className="text-teal-800 font-semibold">Classroom/Age Group</Label>
            <Select value={selectedClassroom} onValueChange={(val) => {
              setSelectedClassroom(val);
              // Reset mixed classrooms when switching
              if (val !== 'mixed_age_group') {
                setMixedClassrooms([]);
              }
            }}>
              <SelectTrigger className="border-teal-200 mt-1">
                <SelectValue placeholder="Select classroom/age group" />
              </SelectTrigger>
              <SelectContent>
                {CLASSROOMS.map(cls => (
                  <SelectItem key={cls.value} value={cls.value}>{cls.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-teal-800 font-semibold">Category</Label>
            <Select value={selectedCategory} onValueChange={(val) => {
              setSelectedCategory(val);
              // Reset mixed categories when switching
              if (val !== 'mixed_category') {
                setMixedCategories([]);
              }
            }}>
              <SelectTrigger className="border-teal-200 mt-1">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Mixed Age Group Selection */}
        {isMixedAgeGroup && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg print:hidden">
            <div className="flex items-center gap-2 mb-3">
              <Info className="w-5 h-5 text-blue-600" />
              <Label className="text-blue-800 font-semibold">Select Age Groups to Mix</Label>
            </div>
            <p className="text-sm text-blue-600 mb-3">
              Choose which age groups/classrooms to include:
            </p>
            <div className="flex flex-wrap gap-4">
              {CLASSROOMS.filter(cls => cls.value !== 'mixed_age_group').map(cls => {
                // Count children in this classroom
                const count = children.filter(c => 
                  c.active !== false && 
                  (c.fullTimeSubcategory || '').toLowerCase().replace(/\s+/g, '_') === cls.value
                ).length;
                
                return (
                  <div key={cls.value} className="flex items-center gap-2">
                    <Checkbox
                      id={`mix-classroom-${cls.value}`}
                      checked={mixedClassrooms.includes(cls.value)}
                      onCheckedChange={() => toggleMixedClassroom(cls.value)}
                    />
                    <label 
                      htmlFor={`mix-classroom-${cls.value}`}
                      className="text-sm font-medium cursor-pointer"
                    >
                      {cls.label} 
                      <span className="text-blue-500 ml-1">({count})</span>
                    </label>
                  </div>
                );
              })}
            </div>
            
            {/* Add Mixed Classroom Children Button */}
            <div className="mt-4 flex items-center gap-3">
              <Button
                onClick={handleAddMixedChildrenByClassroom}
                disabled={mixedClassrooms.length === 0}
                className="bg-blue-500 hover:bg-blue-600 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Children from Selected Age Groups
              </Button>
              <span className="text-sm text-blue-600">
                {getMixedFilteredChildrenByClassroom().length} children available
              </span>
            </div>
          </div>
        )}

        {/* Mixed Category Selection */}
        {isMixedCategory && (
          <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg print:hidden">
            <div className="flex items-center gap-2 mb-3">
              <Info className="w-5 h-5 text-purple-600" />
              <Label className="text-purple-800 font-semibold">Select Categories to Mix</Label>
            </div>
            <p className="text-sm text-purple-600 mb-3">
              Choose which categories to include:
            </p>
            <div className="flex flex-wrap gap-4">
              {CATEGORIES.filter(cat => cat.value !== 'mixed_category').map(cat => {
                // Count children in this category
                const count = children.filter(c => 
                  c.active !== false && 
                  (c.category || '').toLowerCase().replace(/\s+/g, '_') === cat.value
                ).length;
                
                return (
                  <div key={cat.value} className="flex items-center gap-2">
                    <Checkbox
                      id={`mix-cat-${cat.value}`}
                      checked={mixedCategories.includes(cat.value)}
                      onCheckedChange={() => toggleMixedCategory(cat.value)}
                    />
                    <label 
                      htmlFor={`mix-cat-${cat.value}`}
                      className="text-sm font-medium cursor-pointer"
                    >
                      {cat.label} 
                      <span className="text-purple-500 ml-1">({count})</span>
                    </label>
                  </div>
                );
              })}
            </div>
            
            {/* Add Mixed Category Children Button */}
            <div className="mt-4 flex items-center gap-3">
              <Button
                onClick={handleAddMixedChildrenByCategory}
                disabled={mixedCategories.length === 0}
                className="bg-purple-500 hover:bg-purple-600 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Children from Selected Categories
              </Button>
              <span className="text-sm text-purple-600">
                {getMixedFilteredChildrenByCategory().length} children available
              </span>
            </div>
          </div>
        )}

        {/* Add Children Buttons - Only show when NOT in mixed modes */}
        {!isMixedAgeGroup && !isMixedCategory && (
          <div className="mb-6 flex flex-wrap gap-4 print:hidden">
            {/* Add by Classroom/Age Group */}
            <div className="flex items-center gap-2">
              <Button
                onClick={handleAddChildrenByClassroom}
                disabled={!selectedClassroom}
                className="bg-blue-500 hover:bg-blue-600 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add from {getClassroomName(selectedClassroom) || 'Age Group'}
              </Button>
              <span className="text-sm text-gray-600">
                {getFilteredChildrenByClassroom().length} selected
              </span>
            </div>
            
            {/* Add by Category */}
            <div className="flex items-center gap-2">
              <Button
                onClick={handleAddChildrenByCategory}
                disabled={!selectedCategory}
                className="bg-teal-500 hover:bg-teal-600 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add from {getCategoryName(selectedCategory) || 'Category'}
              </Button>
              <span className="text-sm text-gray-600">
                {getFilteredChildrenByCategory().length} selected
              </span>
            </div>
          </div>
        )}

        {/* Report Preview */}
        <div className="border-2 border-teal-100 rounded-lg p-6 bg-white print:border-0 print:p-0">
          <div className="text-center mb-6 border-b-2 border-teal-200 pb-4 print:border-black">
            <h2 className="text-2xl font-bold text-teal-800 print:text-black">Daily Roster</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-left">
              <div>
                <span className="font-semibold">Staff:</span>
                <p className="text-teal-700 print:text-black">{getStaffName(selectedStaff)}</p>
              </div>
              <div>
                <span className="font-semibold">Classroom:</span>
                <p className="text-teal-700 print:text-black">
                  {isMixedAgeGroup 
                    ? mixedClassrooms.map(c => getClassroomName(c)).join(', ') || 'Not Selected'
                    : getClassroomName(selectedClassroom) || 'Not Selected'}
                </p>
              </div>
              <div>
                <span className="font-semibold">Category:</span>
                <p className="text-teal-700 print:text-black">
                  {isMixedCategory 
                    ? mixedCategories.map(c => getCategoryName(c)).join(', ') || 'Not Selected'
                    : getCategoryName(selectedCategory) || 'Not Selected'}
                </p>
              </div>
              <div>
                <span className="font-semibold">Total Children:</span>
                <p className="text-teal-700 print:text-black text-center">{assignedChildren.length}</p>
              </div>
            </div>
          </div>

          {/* Assigned Children List */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-lg text-teal-800 print:text-black">
                My Assigned Children
              </h3>
              {assignedChildren.length > 0 && (
                <Button
                  onClick={handleClearAll}
                  variant="outline"
                  size="sm"
                  className="text-red-500 border-red-200 hover:bg-red-50 print:hidden"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Clear All
                </Button>
              )}
            </div>
            
            {assignedChildren.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <Users className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                <p className="text-gray-500 italic">
                  No children assigned. Select a classroom/age group or category and click "Add" to add children.
                </p>
              </div>
            ) : (
              <ol className="list-decimal list-inside space-y-2">
                {assignedChildren.map((child, index) => (
                  <li key={child.id || child.childId || index} className="flex items-center justify-between bg-teal-50 p-3 rounded print:bg-white print:border-b">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-medium">
                        <span className="text-teal-600 print:text-black">{index + 1}.</span> {child.name || child.childName}
                      </span>
                      {/* Category Badge */}
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded font-medium">
                        {child.assignedCategory || getCategoryName(child.category)}
                      </span>
                      {/* Classroom/Age Group Badge */}
                      {(child.assignedClassroom || child.classroom) && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-medium">
                          {child.assignedClassroom || getClassroomName(child.classroom)}
                        </span>
                      )}
                      {child.age && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-medium">
                          {child.age}
                        </span>
                      )}
                      {child.birthdate && (
                        <span className="text-xs text-gray-500">
                          DOB: {child.birthdate}
                        </span>
                      )}
                    </div>
                    <Button
                      onClick={() => handleRemoveChild(child.id || child.childId)}
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:bg-red-50 print:hidden"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </li>
                ))}
              </ol>
            )}
          </div>

          {/* Notes */}
          <div>
            <Label className="text-teal-800 font-semibold print:text-black">Notes / Additional Information</Label>
            <Textarea
              value={staffNotes}
              onChange={(e) => setStaffNotes(e.target.value)}
              placeholder="Enter any additional notes..."
              className="border-teal-200 mt-2 min-h-[100px] print:border-black"
            />
          </div>

          {/* Created date + last modified note */}
          <div className="mt-4 space-y-1">
            <p className="text-xs text-slate-400 print:text-black print:text-xs">
              Roster created: {rosterDate}
            </p>
            {lastModifiedByName && lastModifiedByName !== (staff.find(s => s.id === (canViewAllStaff ? selectedStaff : currentUser?.id))?.name) && (
              <p className="text-xs text-amber-600 print:text-black print:text-xs">
                Last updated by {lastModifiedByName} ({lastModifiedByRole === 'owner' ? 'Director' : lastModifiedByRole === 'co-owner' ? 'Supervisor/Admin' : lastModifiedByRole})
                {lastModifiedAt && ` — ${(() => { try { return format(parseISO(lastModifiedAt), 'MMMM d, yyyy'); } catch { return ''; } })()}`}
              </p>
            )}
          </div>
        </div>

        {/* Print Button */}
        <div className="mt-6 flex justify-end gap-2 print:hidden">
          {hasSavedRoster && (
            <Button
              onClick={handleClear}
              disabled={isClearing}
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50"
              data-testid="clear-roster-btn"
            >
              <X className="w-4 h-4 mr-2" />
              {isClearing ? "Clearing..." : "Clear Roster"}
            </Button>
          )}
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="bg-teal-600 hover:bg-teal-700 text-white"
            data-testid="save-roster-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {isSaving ? "Saving..." : hasSavedRoster ? "Update Roster" : "Save Roster"}
          </Button>
          <Button 
            onClick={() => exportStaffAssignmentPDF({
              staffName: getStaffName(selectedStaff),
              classroom: isMixedAgeGroup 
                ? mixedClassrooms.map(c => getClassroomName(c)).join(', ')
                : getClassroomName(selectedClassroom),
              category: isMixedCategory 
                ? mixedCategories.map(c => getCategoryName(c)).join(', ')
                : getCategoryName(selectedCategory),
              date: format(new Date(), 'yyyy-MM-dd'),
              assignedChildren: assignedChildren.map(child => ({
                name: child.name,
                birthdate: child.birthdate,
                age: child.age,
                category: child.assignedCategory || getCategoryName(child.category),
                classroom: child.assignedClassroom || ''
              })),
              notes: staffNotes
            }, centerName)}
            className="bg-purple-600 hover:bg-purple-700 text-white"
          >
            <Download className="w-4 h-4 mr-2" />
            Export PDF
          </Button>
          <Button onClick={handlePrint} className="bg-teal-600 hover:bg-teal-700 text-white">
            <Printer className="w-4 h-4 mr-2" />
            Print Report
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default StaffAssignmentTab;
